/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IcDn, IcListL, IcSrchL, IcTick } from "../tournamentPage/icons";
import { useTournamentMatches } from "../tournamentPage/useTournamentMatches";
import { buildAllVms, startsIn, type MatchVm } from "../tournamentPage/matchVm";
import { norm, resultPill, teamPath, useLiveTeams, useTournamentTeams } from "../teamsTab/teamsData";
import { FormPills, TeamCrest } from "../teamsTab/TeamsTab";
import { fmtDay, isMultiDay, nrrText, overs, quotientText, useGroupTables, useTournamentInfo, type PtRow } from "./tournamentData";
import "../tournamentPage/tournamentPage.scss";
import "../teamsTab/teamsTab.scss";
import "./tournamentTabs.scss";

type Props = { tournamentId: string; tournamentName: string };

const Chev = ({ open }: { open: boolean }) => <svg className={`pchev${open ? " open" : ""}`} width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;

/** one finished match from a team's side */
const sideOf = (m: MatchVm, name: string) => {
  const mine = norm(m.a.name) === norm(name) ? m.a : m.b;
  const opp = mine === m.a ? m.b : m.a;
  const r = m.outcome === "NR" ? "NR" : m.outcome === "T" ? "T" : mine.won ? "W" : mine.lost ? "L" : "NR";
  return { mine, opp, r: resultPill(r) };
};
const sc = (t: MatchVm["a"]) => (t.score ? `${t.score}${t.overs ? ` (${t.overs.split("/")[0]})` : ""}` : "DNB");

const PointsTab = ({ tournamentId }: Props) => {
  const navigate = useNavigate();
  const teamsQ = useTournamentTeams(tournamentId);
  const infoQ = useTournamentInfo(tournamentId);
  const all = useTournamentMatches(tournamentId);
  const vms = useMemo(() => buildAllVms(all.live, all.up, all.past), [all.live, all.up, all.past]);
  const live = useLiveTeams(tournamentId).data;
  const divisions = teamsQ.data?.divisions ?? [];
  const teams = teamsQ.data?.teams ?? [];
  const info = infoQ.data;
  const isTest = isMultiDay(info);
  const bonus = !!info?.bonusPointsEnabled;

  const [divId, setDivId] = useState("");
  const [groupId, setGroupId] = useState("all");
  const [menu, setMenu] = useState<"div" | "group" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const div = divisions.find((d) => d.id === divId) ?? divisions[0];
  const groups = useMemo(() => (div ? (div.groups.length ? div.groups : [{ id: div.id, name: "" }]) : []), [div]);
  const tablesQ = useGroupTables(div?.id);

  useEffect(() => {
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const shownGroups = groups.filter((g) => groupId === "all" || g.id === groupId);
  const rowsOf = (gid: string): PtRow[] => {
    const rows = [...(tablesQ.data?.[gid] ?? [])];
    return rows.sort((a, b) => (a.rank && b.rank ? a.rank - b.rank : 0) || b.pts + b.bonus - (a.pts + a.bonus) || (b.nrr ?? -99) - (a.nrr ?? -99) || a.name.localeCompare(b.name));
  };
  const allRows = shownGroups.flatMap((g) => rowsOf(g.id));
  const teamOf = (r: PtRow) => teams.find((t) => t.snapshotId === r.teamSnapshotId);
  const liveOf = (name: string) => !!live && live.names.has(norm(name));
  const liveMatches = vms.filter((m) => m.bucket === "live" && allRows.some((r) => [m.a.name, m.b.name].some((n) => norm(n) === norm(r.name))));
  const anyPlayed = allRows.some((r) => r.m > 0);
  const total = vms.length;
  const finished = total > 0 && vms.every((m) => m.bucket === "past");

  const notice = total === 0 ? <><b>Fixtures not out yet.</b> Every team starts on 0 points. The table updates after each result.</>
    : !anyPlayed ? <><b>No results yet.</b> The table updates as soon as the first match ends.</>
    : liveMatches.length ? <><b>Live now: {liveMatches.map((m) => `${m.a.name} v ${m.b.name}`).join(", ")}.</b> {liveMatches.length === 1 ? "This match isn't" : "These matches aren't"} in the table yet. Points are added when {liveMatches.length === 1 ? "it ends" : "each one ends"}.</>
    : finished ? <><b>Final standings.</b> All matches of this tournament are complete.</>
    : null;

  const detail = (r: PtRow) => {
    const mine = vms.filter((m) => m.bucket === "past" && [m.a.name, m.b.name].some((n) => norm(n) === norm(r.name))).sort((a, b) => b.ms - a.ms);
    const next = vms.filter((m) => m.bucket === "up" && [m.a.name, m.b.name].some((n) => norm(n) === norm(r.name))).sort((a, b) => a.ms - b.ms)[0];
    return (
      <div className="pdet">
        {mine.length === 0 ? <p className="dnote">Yet to play a match.</p> : mine.map((m) => {
          const s = sideOf(m, r.name);
          return (
            <button key={m.id} className="pdm" onClick={() => navigate(`/matches-details/${m.id}`)}>
              <b className={`fp ${s.r}`}>{s.r}</b>
              <span className="pdw"><b>{m.label || `Match ${m.no}`} · v {s.opp.name}</b><small>{m.start ? fmtDay(m.start) : ""}</small></span>
              <span className="pds"><em>{sc(s.mine)}</em><em>{sc(s.opp)}</em></span>
              <span className="pdr">{m.outcome === "NR" ? "No result" : m.outcome === "T" ? "Tied" : s.r === "W" ? m.marginTitle.replace(/^By /, "Won by ") : s.r === "L" ? m.marginTitle.replace(/^By /, "Lost by ") : m.resultTitle}</span>
            </button>
          );
        })}
        <div className="pdf">
          <span>{next ? <>Next: v {norm(next.a.name) === norm(r.name) ? next.b.name : next.a.name} · {startsIn(next.start).when}, {next.time}</> : null}</span>
          {r.teamSnapshotId && <button className="btn" onClick={() => navigate(teamPath(tournamentId, r.teamSnapshotId))}>View team</button>}
        </div>
      </div>
    );
  };

  if (teamsQ.isLoading) return <div className="tp pt"><main className="wrap main"><div className="box"><div style={{ padding: 18, display: "grid", gap: 10 }}>{Array.from({ length: 8 }, (_, i) => <i key={i} className="sk" style={{ height: 26 }} />)}</div></div></main></div>;
  if (teamsQ.isError)
    return <div className="tp pt"><main className="wrap main"><div className="empty"><span className="ic"><IcSrchL /></span><h3>Couldn't load this</h3><p>Check your connection and try again. Nothing you've saved is lost.</p><div className="acts"><button className="btn primary" onClick={() => void teamsQ.refetch()}>Try again</button></div></div></main></div>;
  if (teams.length === 0)
    return <div className="tp pt"><main className="wrap main"><div className="empty"><span className="ic"><IcListL /></span><h3>No points table yet</h3><p>The table appears once teams are added to the tournament.</p></div></main></div>;

  const cols = (isTest ? 9 : 12) + (bonus ? 1 : 0);
  const pos = (rows: PtRow[], i: number) => {
    const r = rows[i];
    if (!r.m) return "–";
    let j = i;
    while (j > 0 && rows[j - 1].pts + rows[j - 1].bonus === r.pts + r.bonus && (rows[j - 1].nrr ?? 0) === (r.nrr ?? 0)) j--;
    return j === i && !(i + 1 < rows.length && rows[i + 1].pts + rows[i + 1].bonus === r.pts + r.bonus && (rows[i + 1].nrr ?? 0) === (r.nrr ?? 0)) ? String(i + 1) : `=${j + 1}`;
  };

  return (
    <div className="tp pt">
      <main className="wrap main">
        <div className="tool">
          <div className="dd">
            <button className="ddb" aria-haspopup="listbox" aria-expanded={menu === "div"} onClick={(e) => { e.stopPropagation(); setMenu(menu === "div" ? null : "div"); }}>{div?.name ?? "Division"}<IcDn /></button>
            {menu === "div" && (
              <div className="ddm" role="listbox" onClick={(e) => e.stopPropagation()}>
                {divisions.map((d) => (
                  <button key={d.id} role="option" aria-selected={div?.id === d.id} className={div?.id === d.id ? "on" : ""} onClick={() => { setDivId(d.id); setGroupId("all"); setOpen(null); setMenu(null); }}>{d.name}<span className="m">{teams.filter((t) => t.divisionId === d.id).length} teams</span>{div?.id === d.id && <span className="ck"><IcTick /></span>}</button>
                ))}
                {divisions.length < 2 && <><hr /><div className="ddnote">This tournament has one division.</div></>}
              </div>
            )}
          </div>
          {div && div.groups.length > 0 && (
            <div className="dd">
              <button className="ddb" aria-haspopup="listbox" aria-expanded={menu === "group"} onClick={(e) => { e.stopPropagation(); setMenu(menu === "group" ? null : "group"); }}>{div.groups.find((g) => g.id === groupId)?.name ?? "All groups"}<IcDn /></button>
              {menu === "group" && (
                <div className="ddm" role="listbox" onClick={(e) => e.stopPropagation()}>
                  <button role="option" aria-selected={groupId === "all"} className={groupId === "all" ? "on" : ""} onClick={() => { setGroupId("all"); setOpen(null); setMenu(null); }}>All groups{groupId === "all" && <span className="ck"><IcTick /></span>}</button>
                  {div.groups.map((g) => <button key={g.id} role="option" aria-selected={groupId === g.id} className={groupId === g.id ? "on" : ""} onClick={() => { setGroupId(g.id); setOpen(null); setMenu(null); }}>{g.name}<span className="m">{teams.filter((t) => t.groupId === g.id).length} teams</span></button>)}
                  {div.groups.length < 2 && <><hr /><div className="ddnote">This division has one group.</div></>}
                </div>
              )}
            </div>
          )}
          <span style={{ flex: 1 }} />
          <div className="legendwl"><span><b className="fp W">W</b>Won</span><span><b className="fp L">L</b>Lost</span><span><b className="fp T">T</b>Tied</span><span><b className="fp NR">NR</b>No result</span></div>
        </div>

        {notice && <div className="pnotice" role="status">{notice}</div>}
        {(tablesQ.isLoading || infoQ.isLoading) && <div className="box" style={{ marginTop: 16 }}><div style={{ padding: 18, display: "grid", gap: 10 }}>{Array.from({ length: 6 }, (_, i) => <i key={i} className="sk" style={{ height: 24 }} />)}</div></div>}
        {tablesQ.isError && <div className="empty"><span className="ic"><IcSrchL /></span><h3>Couldn't load this</h3><p>Check your connection and try again.</p><div className="acts"><button className="btn primary" onClick={() => void tablesQ.refetch()}>Try again</button></div></div>}

        {!tablesQ.isLoading && !infoQ.isLoading && !tablesQ.isError && shownGroups.map((g) => {
          const rows = rowsOf(g.id);
          const title = g.name ? `${div?.name} · ${g.name}` : div?.name;
          return (
            <section key={g.id} className="pgroup">
              <div className="day"><h3>{title}</h3><span>{rows.length} {rows.length === 1 ? "team" : "teams"}</span></div>
              {rows.length === 0 ? <div className="hempty"><b>No points table yet</b><span>It appears once teams are added to this group.</span></div> : (
                <div className="box"><div className="tw"><table className="ptt">
                  <thead><tr>
                    <th className="l">#</th><th className="l tcol">Team</th><th title="Matches played">M</th><th title="Won">W</th><th title="Lost">L</th><th title={isTest ? "Drawn" : "Tied"}>{isTest ? "D" : "T"}</th>{!isTest && <th title="No result">NR</th>}
                    {bonus && <th title="Bonus points">Bonus</th>}<th className="ptc" title="Points">Pts</th><th title={isTest ? "Quotient" : "Net run rate"}>{isTest ? "Quotient" : "NRR"}</th>
                    {!isTest && <th title="Runs scored / overs faced">For</th>}{!isTest && <th title="Runs conceded / overs bowled">Against</th>}<th className="l">Last 5</th><th />
                  </tr></thead>
                  <tbody>{rows.map((r, i) => {
                    const t = teamOf(r);
                    const key = r.teamSnapshotId || r.name;
                    const isOpen = open === key;
                    return (
                      <Fragment key={key}>
                        <tr className={`prow${isOpen ? " open" : ""}`} onClick={() => setOpen(isOpen ? null : key)}>
                          <td className="l ps">{pos(rows, i)}</td>
                          <td className="l tcol"><span className="ptm"><TeamCrest t={{ name: r.name, code: t?.code ?? r.name.slice(0, 3).toUpperCase(), color: t?.color ?? "#29276B", image: t?.image }} size={28} /><b>{r.name}</b>{liveOf(r.name) && <span className="flag live"><i />LIVE</span>}</span></td>
                          <td>{r.m}</td><td>{r.w}</td><td>{r.l}</td><td>{isTest ? r.d : r.t}</td>{!isTest && <td>{r.nr}</td>}
                          {bonus && <td>{r.bonus}</td>}<td className="ptc">{r.pts + r.bonus}</td>
                          <td className={isTest || r.nrr == null ? "" : r.nrr > 0 ? "pos" : r.nrr < 0 ? "neg" : ""}>{r.m ? (isTest ? quotientText(r.nrr) : nrrText(r.nrr)) : "–"}</td>
                          {!isTest && <td>{r.forBalls ? `${r.forRuns}/${overs(r.forBalls)}` : "–"}</td>}{!isTest && <td>{r.againstBalls ? `${r.againstRuns}/${overs(r.againstBalls)}` : "–"}</td>}
                          <td className="l">{r.form.length ? <FormPills form={r.form} /> : <i className="none">–</i>}</td>
                          <td className="pc"><button className="pcb" aria-expanded={isOpen} aria-label={`${isOpen ? "Hide" : "Show"} ${r.name} results`} onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : key); }}><Chev open={isOpen} /></button></td>
                        </tr>
                        {isOpen && <tr className="pdrow"><td colSpan={cols + 3}>{detail(r)}</td></tr>}
                      </Fragment>
                    );
                  })}</tbody>
                </table></div></div>
              )}
            </section>
          );
        })}

        <div className="pkey">
          <div><b>Points</b><span>{isTest
            ? `Outright win ${info?.pointForOutrightWin ?? "–"} · First-innings lead ${info?.pointForFirstInningsLead ?? "–"} · Draw ${info?.pointForTie ?? "–"}`
            : `Win ${info?.pointForWin ?? 2} · Tie ${info?.pointForTie ?? 1} · No result ${info?.pointForAbandoned ?? 1} · Loss 0`}</span></div>
          <div><b>{isTest ? "Quotient" : "NRR"}</b><span>{isTest ? "Teams level on points are separated by quotient." : "Net run rate = runs scored per over − runs conceded per over. A team bowled out counts its full overs. No-result matches are left out."}</span></div>
          {!isTest && <div><b>For / Against</b><span>Runs / overs.</span></div>}
          <div><b>Details</b><span>Click a row to see that team's results.</span></div>
        </div>
      </main>
    </div>
  );
};

export default PointsTab;
